'use client'

import React, { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { X } from 'phosphor-react'
import { ArrowLeft, Heart, SquaresFour, Play } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'

export default function VolunteerCommunityDesign() {
  const [openCarouselIndex, setOpenCarouselIndex] = useState<number | null>(null)

  const projects: Array<{ title: string; image?: string; video?: string; poster?: string; carousel?: Array<{ src: string; alt: string }>; description: string; year?: string; organization?: string; url?: string; type: 'volunteer' | 'design' }> = [
    // Design Projects
    {
      title: 'Midway Neighborhood School Fundraiser',
      image: '/assets/creative-projects/Midway-T-Shirt-Actual.png',
      carousel: [
        { src: '/assets/creative-projects/Midway-T-Shirt-Actual.png', alt: 'Midway T-Shirt Design' },
        { src: '/assets/creative-projects/Gator_Chomps1.png', alt: 'Gator Chomps Design' },
        { src: '/assets/creative-projects/Artboard 1Midway T-Shirts.pdf', alt: 'Midway T-Shirt Artboard 1' },
        { src: '/assets/creative-projects/Artboard 2Midway T-Shirts.pdf', alt: 'Midway T-Shirt Artboard 2' },
        { src: '/assets/creative-projects/Artboard 3Midway T-Shirts.pdf', alt: 'Midway T-Shirt Artboard 3' }
      ],
      description: 'Design for Fundraising Merchandise for Midway neighborhood school.',
      year: '2020',
      type: 'design'
    },
    {
      title: 'A Midsummer Night\'s Delirium',
      image: '/assets/creative-projects/Midsummer-Nights-Delirium_A3.png',
      description: 'Promotional poster for a circus event featuring fire performers, aerialists, and theatrical imagery with Art Nouveau-inspired framing',
      year: '2025',
      type: 'design'
    },
    {
      title: 'Writhing: An Anthology - Cover Design',
      image: '/assets/creative-projects/Front Cover - Final (ebook).png',
      description: 'Book cover design for Wordplay Writer\'s group anthology. Learn more about Wordplay: @wordplay.openmic',
      year: '2025',
      type: 'design',
      url: 'https://wordplay.pt'
    },
    // Volunteer projects
    {
      title: 'Wordplay Writers Club Website',
      organization: 'Wordplay Writers Club',
      image: '/assets/creative-projects/wordplay-website.png',
      description: 'Website for a Lisbon-area writers community — events, gallery, and a welcoming home for writers at every level.',
      year: '2025',
      type: 'volunteer',
      url: 'https://wordplay.pt'
    },
    {
      title: 'Ready Play',
      video: 'videos/Ready Play_v1.mp4',
      poster: '/assets/creative-projects/Ready-Play-poster.png',
      description: 'Summer camp application for parents to find activities for their children.',
      year: '2019',
      type: 'volunteer'
    },
    {
      title: 'Slacktivist',
      video: 'videos/Slacktivist.mp4',
      poster: '/assets/creative-projects/Slacktivist-poster.png', // Will fallback to gradient if not found
      description: 'Activism app for the nonprofit organizations to recruit volunteers.',
      year: '2019',
      type: 'volunteer'
    }
  ]

  const volunteerProjects = projects.filter(p => p.type === 'volunteer')
  const designProjects = projects.filter(p => p.type === 'design')

  return (
    <Components.PageContainer>
      <Components.SkipLink />
      <Components.GlassNavigation />
      
      <main id="main-content" tabIndex={-1} className="pt-16 md:pt-32 pb-16 px-4 md:px-6" role="main" aria-label="Volunteer & Community Design Work">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="mb-8 md:mb-12">
            <Button
              asChild
              variant="ghost"
              className="mb-6 text-[var(--brutal-ink)] hover:bg-[var(--brutal-panel)]"
            >
              <Link to="/CreativeProjects">
                <ArrowLeft weight="duotone" className="w-4 h-4 mr-2" />
                Back to Creative Projects
              </Link>
            </Button>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 aspect-square bg-gradient-to-br from-pink-500/20 to-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Heart weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--brutal-ink)] leading-tight break-words">
                  Volunteer & Community Design Work
                </h1>
                <p className="text-base md:text-lg text-[var(--brutal-muted)] mt-2 break-words">
                  Pro bono design projects for local nonprofits and community organizations, personal graphic and visual design projects, experimental typography, and visual communication work.
                </p>
              </div>
            </div>
          </header>

          {/* Design Projects Section */}
          {designProjects.length > 0 && (
            <section className="mb-12" aria-labelledby="design-heading">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-6 h-6 aspect-square flex items-center justify-center flex-shrink-0">
                        <SquaresFour weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                      </div>
                      <h2 id="design-heading" className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)]">
                        Personal Design Projects
                      </h2>
                    </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {designProjects.map((project, index) => {
                  const hasCarousel = project.carousel && project.carousel.length > 0
                  const isCarouselOpen = openCarouselIndex === index
                  
                  return (
                    <Card
                      key={index}
                      className="group relative overflow-hidden brutal-panel hover:bg-[var(--brutal-kraft)] transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl !p-0 !gap-0"
                    >
                      {project.image ? (
                        hasCarousel ? (
                          <>
                            <div 
                              className="relative aspect-[4/3] overflow-hidden border-b-2 border-[var(--brutal-ink)] cursor-pointer"
                              onClick={() => setOpenCarouselIndex(index)}
                            >
                              <img
                                src={project.image}
                                alt={project.title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>
                            <Dialog open={isCarouselOpen} onOpenChange={(open) => setOpenCarouselIndex(open ? index : null)}>
                              <DialogContent 
                                showCloseButton={false}
                                className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/98 border-0 rounded-lg overflow-hidden p-0 !w-auto !h-auto !max-w-none sm:!max-w-[90vw] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                              >
                                <div className="relative flex items-center justify-center">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white border-0 rounded-full w-10 h-10"
                                    onClick={() => setOpenCarouselIndex(null)}
                                  >
                                    <X weight="duotone" className="w-5 h-5" />
                                  </Button>
                                  <div className="p-4 overflow-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                                    <Components.ImageCarousel
                                      images={project.carousel!.map(item => ({
                                        src: item.src,
                                        alt: item.alt,
                                        title: item.alt
                                      }))}
                                      className="w-full"
                                    />
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </>
                        ) : (
                          <Components.ImageModal
                            src={project.image}
                            alt={project.title}
                          >
                            <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-[var(--brutal-ink)] cursor-pointer">
                              <img
                                src={project.image}
                                alt={project.title}
                                className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${
                                  project.title === 'Writhing: An Anthology - Cover Design' ? 'object-top object-left' : ''
                                }`}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>
                          </Components.ImageModal>
                        )
                      ) : null}
                      <div className="p-4">
                        <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-1 group-hover:text-[var(--brutal-ink)] transition-colors duration-200 break-words">
                          {project.title}
                        </h3>
                        {project.year && (
                          <p className="text-[var(--brutal-muted)] text-xs mb-2">{project.year}</p>
                        )}
                        <p className="text-[var(--brutal-muted)] text-sm leading-relaxed break-words">
                          {project.title === 'Writhing: An Anthology - Cover Design' ? (
                            <>
                              Book cover design for Wordplay Writer's group anthology. Learn more about Wordplay:{' '}
                              <a
                                href="https://wordplay.pt"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[var(--brutal-ink)] hover:text-[var(--brutal-muted)] underline transition-colors"
                              >
                                wordplay.pt
                              </a>
                              {' · '}
                              <a 
                                href="https://www.instagram.com/wordplay.openmic" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[var(--brutal-ink)] hover:text-[var(--brutal-muted)] underline transition-colors"
                              >
                                @wordplay.openmic
                              </a>
                            </>
                          ) : (
                            project.description
                          )}
                        </p>
                      </div>
                    </Card>
                  )
                })}
              </div>
            </section>
          )}

          {/* Volunteer Projects Section */}
          {volunteerProjects.length > 0 && (
            <section className="mb-12" aria-labelledby="volunteer-heading">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-6 h-6 aspect-square flex items-center justify-center flex-shrink-0">
                  <Heart weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                </div>
                <h2 id="volunteer-heading" className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)]">
                  Volunteer & Community Projects
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {volunteerProjects.map((project, index) => (
                  <Card
                    key={index}
                    className="group relative overflow-hidden brutal-panel hover:bg-[var(--brutal-kraft)] transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl !p-0 !gap-0"
                  >
                    {project.video ? (
                      <Components.VideoModal
                        dataPath={project.video.startsWith('videos/') ? project.video : `videos/${project.video}`}
                        alt={project.title}
                        poster={project.poster}
                        requireAuth={false}
                        className="w-full"
                      >
                        {project.poster ? (
                          <div className="relative aspect-video overflow-hidden border-b-2 border-[var(--brutal-ink)] cursor-pointer group">
                            <img 
                              src={project.poster} 
                              alt={`${project.title} video thumbnail`}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            {/* Play button overlay */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                                <Play weight="duotone" className="w-12 h-12 text-[var(--brutal-ink)]" />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="relative aspect-video overflow-hidden border-b-2 border-[var(--brutal-ink)] bg-black/20 cursor-pointer group">
                            <div className="w-full h-full bg-gradient-to-br from-slate-600/20 to-slate-800/20 flex items-center justify-center">
                              <Play weight="duotone" className="w-16 h-16 text-[var(--brutal-muted)]" />
                            </div>
                            {/* Play button overlay */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                                <Play weight="duotone" className="w-12 h-12 text-[var(--brutal-ink)]" />
                              </div>
                            </div>
                          </div>
                        )}
                      </Components.VideoModal>
                    ) : project.image ? (
                      <Components.ImageModal
                        src={project.image}
                        alt={project.title}
                      >
                        <div className="relative aspect-video overflow-hidden border-b-2 border-[var(--brutal-ink)] cursor-pointer">
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                      </Components.ImageModal>
                    ) : null}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-2 group-hover:text-[var(--brutal-ink)] transition-colors duration-200 break-words">
                        {project.title}
                      </h3>
                      {project.organization && (
                        <p className="text-[var(--brutal-ink)] text-sm mb-2 font-medium">{project.organization}</p>
                      )}
                      {project.year && (
                        <p className="text-[var(--brutal-muted)] text-xs mb-3">{project.year}</p>
                      )}
                      <p className="text-[var(--brutal-muted)] leading-relaxed break-words">
                        {project.title === 'Writhing: An Anthology - Cover Design' ? (
                          <>
                            Book cover design for Wordplay Writer's group anthology. Learn more about Wordplay:{' '}
                            <a
                              href="https://wordplay.pt"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[var(--brutal-ink)] hover:text-[var(--brutal-muted)] underline transition-colors"
                            >
                              wordplay.pt
                            </a>
                            {' · '}
                            <a 
                              href="https://www.instagram.com/wordplay.openmic" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[var(--brutal-ink)] hover:text-[var(--brutal-muted)] underline transition-colors"
                            >
                              @wordplay.openmic
                            </a>
                          </>
                        ) : (
                          project.description
                        )}
                      </p>
                      {project.url && project.title !== 'Writhing: An Anthology - Cover Design' && (
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex mt-4 text-sm font-medium text-[var(--brutal-ink)] hover:text-[var(--brutal-ink)] underline underline-offset-4 transition-colors"
                        >
                          Visit {project.url.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* Empty State */}
          {projects.length === 0 && (
            <Card className="brutal-panel p-12 text-center">
              <p className="text-[var(--brutal-muted)] text-lg">Projects coming soon...</p>
            </Card>
          )}
        </div>
      </main>
    </Components.PageContainer>
  )
}
